import React, { useEffect, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Paper,
  Button,
  IconButton,
  Chip,
  Switch,
  TextField,
  Alert,
  Tooltip,
} from '@mui/material';
import { Modal } from '../../../../../../ui/modal/modal';
import { Skill } from '../../../../../../utils/types/JobOpenings';
import { Plus, X, Edit2, ArrowUpDown, Tag, Zap, Briefcase, Brain, Hash } from 'lucide-react';

export type SkillWeightingProps = {
  title: string;
  attributeName: string;
  isWeightRequired?: boolean;
  extractedSkill: Skill[];
  setExtractedSkill: (
    value: ((prevState: Skill[]) => Skill[]) | Skill[]
  ) => void;
  weightingEnabled: boolean;
  setWeightingEnabled: (
    value: ((prevState: boolean) => boolean) | boolean
  ) => void;
  similarEnabled: boolean;
  setSimilarEnabled: (
    value: ((prevState: boolean) => boolean) | boolean
  ) => void;
};

export type SortOption = 'default' | 'highest' | 'lowest';

const DUMMY_SIMILAR_TERMS: Record<string, string[]> = {
  // Skills
  javascript: ['js', 'node.js', 'ecmascript'],
  react: ['react.js', 'reactjs'],
  python: ['py', 'python3'],
  html: ['html5'],
  css: ['css3', 'scss', 'sass'],
  java: ['j2ee', 'openjdk'],
  sql: ['mysql', 'postgresql', 't-sql'],
  git: ['github', 'gitlab', 'version control'],
  mongo: ['mongodb', 'nosql'],
  aws: ['amazon web services', 'cloud'],
  docker: ['containerization', 'containers'],
  typescript: ['ts'],

  // Job Titles
  developer: ['software engineer', 'programmer', 'coder', 'swe'],
  'software engineer': ['developer', 'swe', 'sde', 'engineer'],
  manager: ['lead', 'director', 'supervisor', 'head'],
  'product manager': ['pm', 'product owner'],
  designer: ['ui/ux', 'creative director', 'artist'],

  // Behavioral Attributes
  teamwork: ['collaboration', 'cooperation', 'team player'],
  communication: ['verbal skills', 'written communication', 'articulation'],
  'attention to detail': ['precision', 'accuracy', 'meticulousness'],
  leadership: ['mentorship', 'management', 'guidance'],
  'problem solving': ['analytical skills', 'troubleshooting'],
  adaptability: ['flexibility', 'resilience'],

  // Keywords
  'b2b sales': ['business to business', 'enterprise sales'],
  saas: ['software as a service', 'cloud services'],
  agile: ['scrum', 'kanban', 'lean'],
};

export const SkillWeighting: React.FC<SkillWeightingProps> = ({
  title,
  attributeName,
  isWeightRequired = false,
  extractedSkill,
  setExtractedSkill,
  weightingEnabled,
  setWeightingEnabled,
  similarEnabled,
  setSimilarEnabled,
}) => {
  const [newSkillName, setNewSkillName] = useState('');
  const [newSkillScore, setNewSkillScore] = useState(10);
  const [sortOption, setSortOption] = useState<SortOption>('default');
  const [toastMessage, setToastMessage] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Similar Terms State
  const [similarTermsMap, setSimilarTermsMap] = useState<Record<string, string[]>>(DUMMY_SIMILAR_TERMS);
  const [editingTerm, setEditingTerm] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  const handleEditSimilarClick = (skillName: string) => {
    const lowerName = skillName.toLowerCase();
    const currentTerms = similarTermsMap[lowerName] || [];
    setEditingTerm(skillName);
    setEditValue(currentTerms.join(', '));
  };

  const handleSaveSimilarTerms = (closeModal: () => void) => {
    if (editingTerm) {
      const lowerName = editingTerm.toLowerCase();
      const newTerms = editValue
        .split(',')
        .map((term) => term.trim())
        .filter((term) => term.length > 0);

      setSimilarTermsMap((prev) => ({
        ...prev,
        [lowerName]: newTerms,
      }));
    }
    closeModal();
    setEditingTerm(null);
    setEditValue('');
  };

  useEffect(() => {
    if (!weightingEnabled) return;

    const totalScore = extractedSkill.reduce((sum, s) => sum + s.weight, 0);

    const updatedSkills = extractedSkill.map((s) => {
      const newPercent =
        totalScore > 0 ? Math.round((s.weight / totalScore) * 100) : 0;
      if (s.weightPercent !== newPercent) {
        return { ...s, weightPercent: newPercent };
      }
      return s;
    });

    const hasChanges = updatedSkills.some(
      (s, i) => s.weightPercent !== extractedSkill[i].weightPercent,
    );

    if (hasChanges) {
      setExtractedSkill(updatedSkills);
    }
  }, [extractedSkill, weightingEnabled]);

  const addSkill = () => {
    const trimmedName = newSkillName.trim();
    if (!trimmedName || newSkillScore < 0) return;

    const isDuplicate = extractedSkill.some(
      (skill) => skill.skill.toLowerCase() === trimmedName.toLowerCase(),
    );
    if (isDuplicate) {
      setToastMessage(`"${trimmedName}" is already added.`);
      setTimeout(() => setToastMessage(''), 3000);
      return;
    }

    const updated = [
      ...extractedSkill,
      { skill: trimmedName, weight: newSkillScore, weightPercent: 0 },
    ];
    setExtractedSkill(updated);
    setNewSkillName('');
    setNewSkillScore(10);
  };

  const updateScore = (index: number, newScore: number) => {
    const updated = [...extractedSkill];
    updated[index].weight = newScore;
    setExtractedSkill(updated);
  };

  const updateSkillName = (index: number, newName: string) => {
    const trimmed = newName.trim();
    if (!trimmed) return;

    const isDuplicate = extractedSkill.some(
      (skill, i) =>
        i !== index && skill.skill.toLowerCase() === trimmed.toLowerCase(),
    );

    if (isDuplicate) {
      setToastMessage(`"${trimmed}" already exists.`);
      setTimeout(() => setToastMessage(''), 3000);
      return;
    }

    const updated = [...extractedSkill];
    updated[index].skill = trimmed;
    setExtractedSkill(updated);
  };

  const removeSkillByName = (name: string) => {
    const updated = extractedSkill.filter(
      (skill) => skill.skill.toLowerCase() !== name.toLowerCase(),
    );
    setExtractedSkill(updated);
  };

  const toggleSortOption = () => {
    if (sortOption === 'default') setSortOption('highest');
    else if (sortOption === 'highest') setSortOption('lowest');
    else setSortOption('default');
  };

  const getSimilarTerms = (skillName: string) => {
    const lowerName = skillName.toLowerCase();
    return similarTermsMap[lowerName]?.join(', ') || '';
  };

  const getCategoryTheme = () => {
    const t = title.toLowerCase();
    const attr = attributeName.toLowerCase();
    if (t.includes('x:') || attr.includes('skill')) {
      return {
        badge: 'X AXIS',
        color: '#059669',
        bgColor: '#ecfdf5',
        quickSuggestions: ['React', 'JavaScript', 'TypeScript', 'Node.js', 'Python', 'SQL'],
        emptyDescription: 'Add skills to project candidates on the X-axis',
        icon: <Zap size={16} color='#059669' />,
      };
    }
    if (t.includes('y:') || attr.includes('job') || attr.includes('title')) {
      return {
        badge: 'Y AXIS',
        color: '#2563eb',
        bgColor: '#eff6ff',
        quickSuggestions: ['Software Engineer', 'Frontend Developer', 'Product Manager', 'Team Lead'],
        emptyDescription: 'Add job titles for vertical match scoring',
        icon: <Briefcase size={16} color='#2563eb' />,
      };
    }
    if (t.includes('z:') || attr.includes('attribute') || attr.includes('behavior')) {
      return {
        badge: 'Z AXIS',
        color: '#7c3aed',
        bgColor: '#f5f3ff',
        quickSuggestions: ['Problem Solving', 'Teamwork', 'Communication', 'Attention to Detail'],
        emptyDescription: 'Add behavioral traits for 3D depth analysis',
        icon: <Brain size={16} color='#7c3aed' />,
      };
    }
    return {
      badge: 'KEYWORDS',
      color: '#d97706',
      bgColor: '#fffbeb',
      quickSuggestions: ['Agile', 'B2B', 'SaaS', 'Cloud', 'Microservices'],
      emptyDescription: 'Add weighted keywords for frequency scoring',
      icon: <Hash size={16} color='#d97706' />,
    };
  };

  const theme = getCategoryTheme();

  const addSpecificSkill = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const isDuplicate = extractedSkill.some(
      (skill) => skill.skill.toLowerCase() === trimmed.toLowerCase(),
    );
    if (isDuplicate) return;
    setExtractedSkill([
      ...extractedSkill,
      { skill: trimmed, weight: 10, weightPercent: 0 },
    ]);
  };

  const cleanTitle = title.replace(/^[XYZ]:\s*/i, '');

  return (
    <Paper
      variant='outlined'
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        bgcolor: 'background.paper',
        borderRadius: 2,
        p: 2,
        boxShadow: 1,
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: 2 },
      }}
    >
      {toastMessage && (
        <Alert
          severity='error'
          sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1300,
            boxShadow: 3,
          }}
        >
          {toastMessage}
        </Alert>
      )}

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          pb: 1.5,
          mb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          minHeight: 36,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Chip
            size='small'
            label={theme.badge}
            sx={{
              height: 20,
              fontSize: '0.625rem',
              fontWeight: 700,
              bgcolor: theme.bgColor,
              color: theme.color,
              border: '1px solid',
              borderColor: `${theme.color}40`,
            }}
          />
          <Typography
            variant='subtitle2'
            sx={{
              fontWeight: 700,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {cleanTitle}
          </Typography>
          <Chip
            size='small'
            label={extractedSkill.length}
            sx={{
              height: 18,
              fontSize: '0.6875rem',
              fontWeight: 600,
              bgcolor: extractedSkill.length > 0 ? 'success.light' : 'action.hover',
              color: extractedSkill.length > 0 ? 'success.dark' : 'text.secondary',
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          {isWeightRequired && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant='caption' sx={{ fontWeight: 500, color: 'text.secondary' }}>
                Weight
              </Typography>
              <Switch
                size='small'
                checked={weightingEnabled}
                onChange={(e) => setWeightingEnabled(e.target.checked)}
              />
            </Box>
          )}

          {weightingEnabled && isWeightRequired && (
            <IconButton
              size='small'
              onClick={toggleSortOption}
              title={`Sort: ${sortOption === 'highest' ? 'Highest first' : sortOption === 'lowest' ? 'Lowest first' : 'Default'}`}
              sx={{ p: 0.5 }}
            >
              <ArrowUpDown size={14} color={sortOption !== 'default' ? '#059669' : '#9ca3af'} />
            </IconButton>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant='caption' sx={{ fontWeight: 500, color: 'text.secondary' }}>
              Similar
            </Typography>
            <Switch
              size='small'
              checked={similarEnabled}
              onChange={(e) => setSimilarEnabled(e.target.checked)}
            />
          </Box>
        </Box>
      </Box>

      {/* Skill List */}
      <Box
        sx={{
          flex: 1,
          minHeight: 140,
          maxHeight: 220,
          overflowY: 'auto',
          pr: 0.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {extractedSkill.length === 0 ? (
          <Box
            sx={{
              height: '100%',
              minHeight: 135,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 1.5,
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: 'action.hover',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1,
              }}
            >
              {theme.icon}
            </Box>
            <Typography variant='caption' sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
              No {attributeName}s added yet
            </Typography>
            <Typography variant='caption' sx={{ color: 'text.secondary', mb: 1.5, maxWidth: 200, lineHeight: 1.2 }}>
              {theme.emptyDescription}
            </Typography>

            {/* Quick Add Suggestions */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center', maxWidth: 280 }}>
              {theme.quickSuggestions
                .filter((s) => !extractedSkill.some((x) => x.skill.toLowerCase() === s.toLowerCase()))
                .slice(0, 4)
                .map((suggestion) => (
                  <Chip
                    key={suggestion}
                    size='small'
                    label={`+ ${suggestion}`}
                    onClick={() => addSpecificSkill(suggestion)}
                    clickable
                    sx={{
                      fontSize: '0.6875rem',
                      height: 22,
                      bgcolor: 'action.hover',
                      '&:hover': { bgcolor: 'action.selected' },
                    }}
                  />
                ))}
            </Box>
          </Box>
        ) : (
          [...extractedSkill]
            .sort((a, b) => {
              if (sortOption === 'highest') return b.weight - a.weight;
              if (sortOption === 'lowest') return a.weight - b.weight;
              return 0;
            })
            .map((skill, index) => (
              <Paper
                key={index}
                variant='outlined'
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 1.5,
                  bgcolor: 'action.hover',
                  borderColor: 'divider',
                  '&:hover': {
                    bgcolor: 'action.selected',
                  },
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, mr: 1 }}>
                  <Typography
                    variant='body2'
                    onClick={() => setEditingIndex(index)}
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      cursor: 'pointer',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {skill.skill}
                  </Typography>

                  {/* Similar Terms Display */}
                  {similarEnabled && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.25 }}>
                      <Tag size={10} style={{ color: '#9ca3af', marginRight: 4, flexShrink: 0 }} />
                      <Typography
                        variant='caption'
                        sx={{
                          fontSize: '0.625rem',
                          color: 'text.secondary',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                        }}
                        title={getSimilarTerms(skill.skill)}
                      >
                        {getSimilarTerms(skill.skill) || 'No similar terms'}
                      </Typography>
                      <IconButton
                        size='small'
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditSimilarClick(skill.skill);
                        }}
                        sx={{ p: 0.25, ml: 0.5 }}
                        title='Edit similar terms'
                      >
                        <Edit2 size={10} />
                      </IconButton>
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  {weightingEnabled && isWeightRequired && (
                    <TextField
                      type='number'
                      size='small'
                      inputProps={{ min: 0, style: { padding: '2px 4px', textAlign: 'center', fontSize: '0.75rem', width: 40 } }}
                      value={skill.weight}
                      onChange={(e) => updateScore(index, Number(e.target.value))}
                      onBlur={() => {
                        const updated = [...extractedSkill];
                        setExtractedSkill(updated);
                      }}
                      sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
                    />
                  )}
                  {weightingEnabled && isWeightRequired && (
                    <Chip
                      size='small'
                      label={`${skill.weightPercent}%`}
                      sx={{
                        height: 20,
                        fontSize: '0.625rem',
                        fontWeight: 700,
                        bgcolor: 'success.light',
                        color: 'success.dark',
                      }}
                    />
                  )}
                  <IconButton
                    size='small'
                    onClick={() => removeSkillByName(skill.skill)}
                    sx={{ p: 0.5, color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                    title='Remove'
                  >
                    <X size={14} />
                  </IconButton>
                </Box>
              </Paper>
            ))
        )}
      </Box>

      {/* Add Skill Row */}
      <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          size='small'
          fullWidth
          placeholder={`Add ${attributeName}...`}
          value={newSkillName}
          onChange={(e) => setNewSkillName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addSkill();
            }
          }}
          inputProps={{ style: { fontSize: '0.75rem', padding: '6px 10px' } }}
        />

        {weightingEnabled && isWeightRequired && (
          <TextField
            type='number'
            size='small'
            title='Default weight score'
            value={newSkillScore}
            onChange={(e) => setNewSkillScore(Number(e.target.value))}
            inputProps={{ min: 0, style: { fontSize: '0.75rem', padding: '6px 4px', width: 44, textAlign: 'center' } }}
          />
        )}

        <Button
          size='small'
          variant='contained'
          onClick={addSkill}
          disabled={!newSkillName.trim()}
          sx={{
            minWidth: 54,
            fontSize: '0.75rem',
            textTransform: 'none',
            px: 1.5,
            py: 0.75,
          }}
        >
          Add
        </Button>
      </Box>

      {/* Edit Similar Terms Modal */}
      <Modal
        visible={!!editingTerm}
        toggle={() => setEditingTerm(null)}
        headerText={`Edit Similar Terms for "${editingTerm}"`}
        customClass='!max-w-md !h-auto'
        modalContent={(closeModal) => (
          <Stack spacing={2} sx={{ p: 1 }}>
            <Alert severity='info'>
              Add synonyms or related terms separated by commas. These will be used to broaden the search matching.
            </Alert>

            <Box>
              <Typography variant='caption' sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                Similar Terms (comma separated)
              </Typography>
              <TextField
                multiline
                rows={3}
                fullWidth
                size='small'
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                placeholder='e.g. js, node.js, ecmascript'
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 1 }}>
              <Button
                variant='outlined'
                size='small'
                onClick={closeModal}
              >
                Cancel
              </Button>
              <Button
                variant='contained'
                size='small'
                onClick={() => handleSaveSimilarTerms(closeModal)}
              >
                Save Changes
              </Button>
            </Box>
          </Stack>
        )}
      />
    </Paper>
  );
};

export default SkillWeighting;
